/**
 * ACT2-1124 (F23): the trust-policy text for Redshift with an IAM role in a sibling workspace. Lookout assumes the
 * role with the sibling id as the external ID, so the role's trust policy must accept it. The server builds the
 * text (`redshiftIamTrustPolicyCondition`); a shared-key workspace gets none and this renders nothing.
 */
import { useQuery } from '@tanstack/react-query';

import { CopyButton } from '@/_shared/components/CopyButton';
import { useWorkspaceOrNull } from '@/_shared/contexts/WorkspaceContext';
import { useSyntaxStyle } from '@/_shared/hooks/useSyntaxStyle';
import { getOrpc } from '@/_shared/lib/orpc';
import { codeBlockCustomStyle, SyntaxHighlighter } from '@/_shared/lib/syntax';

import { OutputSection } from '../shared/OutputSection';

export function RedshiftTrustPolicy(): React.JSX.Element | null {
  const slug = useWorkspaceOrNull()?.slug ?? null;
  const syntaxStyle = useSyntaxStyle();
  const { data } = useQuery({
    ...getOrpc(slug ?? '').siblingWorkspace.redshiftTrustPolicy.queryOptions({ input: {} }),
    enabled: slug !== null,
  });
  if (!data?.trustPolicyCondition || !data.externalId) return null;
  return (
    <OutputSection
      label="Trust policy"
      description={`Lookout assumes the role with the external ID ${data.externalId}, the id of this workspace in RudderStack. Add this condition to the role's trust policy.`}
      action={<CopyButton text={data.trustPolicyCondition} label="Copy" copyLabel="Trust policy condition" />}
    >
      <SyntaxHighlighter language="json" style={syntaxStyle} customStyle={codeBlockCustomStyle}>
        {data.trustPolicyCondition}
      </SyntaxHighlighter>
    </OutputSection>
  );
}
